import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

/**
 * SECÇÃO GOOGLE REVIEWS — Ultimate Fit
 * - 100% editável manualmente (sem APIs externas).
 * - Mostra APENAS reviews com texto (filtro garantido).
 * - Desktop: 3 cards em paginação por grupos.
 * - Mobile: 1 card por vez (slider) com pontos navegáveis.
 *
 * IMPORTANTE: usar APENAS reviews reais do Google Business do Ultimate Fit.
 * Não inventar conteúdo. Cada review deve conter:
 *   { id, name, text, rating (1-5), dateLabel (opcional) }
 *
 * Se o array estiver vazio, a secção é automaticamente ocultada.
 */
const REVIEWS = [
  {
    id: 1,
    name: 'Luís Franco',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Espaço bem equipado e com profissionais de excelência. Simpatia e atenção ao cliente acima da média. Recomendo!'
  },
  {
    id: 2,
    name: 'Flávio Lage',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Espaço perfeito para um atendimento exclusivo e eficiente.'
  },
  {
    id: 3,
    name: 'Ana Silva (Isablana)',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Espaço fantástico! Super bem equipado, com os melhores profissionais. Para quem gosta de praticar exercício, encontra treino personalizado, e bons resultados. Ambiente saudável, acolhedor, num contexto favorável à prática de desporto.'
  },
  {
    id: 4,
    name: 'Beatriz Viegas',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Um estúdio fantástico! O ambiente é acolhedor e motivador e a equipa é sempre simpática e profissional. Recomendo vivamente!'
  },
  {
    id: 5,
    name: 'Elisabete Fernandes',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Excelente estúdio de treino personalizado. Profissionais de mão cheia, sempre atentos à execução dos exercícios e muito motivadores. O ambiente é fantástico e a simpatia é a regra da casa. Recomendo a 100% para quem procura um serviço de PT de excelência! Comecei há 2 anos e nunca mais deixei...'
  },
  {
    id: 6,
    name: 'Miguel Sá',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Excelente local para nos mantermos em forma e mais saudáveis. O acompanhamento dos PTs é fantástico: muito profissional e num ambiente muito descontraído mas rigoroso. O espaço está muito agradável, confortável, muito calmo e o mais importante na prática do exercício físico é ser sempre supervisionado. Top.'
  },
  {
    id: 7,
    name: 'Ruben',
    rating: 5,
    dateLabel: 'Google Review',
    text: 'Studio com ambiente profissional, familiar e exclusivo. Boa limpeza organização equipada com material de boa qualidade e variado para permitir diferentes tipos de treino. PT dedicados persistentes e focados nos resultados. No meu caso PT (Manuel Gonzalez). O studio oferece avaliação física a cada mês e avaliações de evolução física pretendida. Studio discreto e elegante.'
  }
];

const GOOGLE_REVIEW_URL = 'https://g.page/r/Cfr67a5siMHXEBI/review';

const titleFont = {
  fontFamily: "'BebasNeue-Book', sans-serif",
  fontWeight: 'normal',
  letterSpacing: '0.02em',
  lineHeight: '1'
};

const bebas = {
  fontFamily: "'BebasNeue-Book', sans-serif",
  fontWeight: 'normal',
  letterSpacing: '0.05em'
};

const bodyFont = {
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 300,
  letterSpacing: '0.01em',
  lineHeight: '1.7'
};

const Reviews = () => {
  // Mostrar APENAS reviews com texto
  const visibleReviews = useMemo(
    () => REVIEWS.filter((r) => r && r.text && r.text.trim().length > 0),
    []
  );

  const [slidesPerView, setSlidesPerView] = useState(3);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setSlidesPerView(w < 768 ? 1 : 3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Total de posições no slider (deslize card-a-card → mais fluido)
  // Ex: 7 reviews, 3 visíveis → 5 posições (1-3, 2-4, 3-5, 4-6, 5-7)
  const totalSlides = Math.max(1, visibleReviews.length - slidesPerView + 1);

  // Sempre que o slidesPerView muda, garantir que index é válido
  useEffect(() => {
    setIndex((prev) => Math.min(prev, totalSlides - 1));
  }, [totalSlides]);

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(i, totalSlides - 1));
    setIndex(clamped);
  };
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  if (visibleReviews.length === 0) return null;

  // Largura de cada card em %
  const cardBasis = `${100 / slidesPerView}%`;
  // translateX: deslocar UM card de cada vez (não a página inteira)
  const translatePct = -(index * (100 / slidesPerView));

  return (
    <section
      id="reviews"
      className="relative bg-white overflow-hidden py-16 sm:py-20 md:py-28"
      data-testid="reviews-section"
    >
      {/* Decoração: balão de comentário, contorno fino, muito subtil */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute select-none"
        style={{
          right: 'clamp(-30px, -2vw, -10px)',
          top: 'clamp(40px, 6vw, 90px)',
          width: 'clamp(180px, 24vw, 360px)',
          height: 'auto',
          opacity: 0.12,
          color: '#000000'
        }}
        viewBox="0 0 200 200"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      >
        <path d="M20 30 H160 V130 H110 L80 165 V130 H20 Z" />
      </svg>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header da secção */}
        <div className="mb-10 md:mb-14">
          <h2
            className="text-black uppercase"
            style={{
              ...titleFont,
              fontSize: 'clamp(36px, 5.5vw, 72px)'
            }}
            data-testid="reviews-title"
          >
            Palavras de quem treina connosco
          </h2>

          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-gray-700 hover:text-black transition-colors uppercase"
            style={{ ...bebas, fontSize: 'clamp(13px, 1vw, 15px)' }}
            data-testid="reviews-sublink"
          >
            <span>Ver todos os comentários</span>
            <GoogleWordmark />
          </a>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Viewport */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(${translatePct}%)` }}
              data-testid="reviews-track"
            >
              {visibleReviews.map((r) => (
                <article
                  key={r.id}
                  className="flex-shrink-0 px-2 sm:px-3 md:px-4"
                  style={{ flexBasis: cardBasis, maxWidth: cardBasis }}
                  data-testid={`review-card-${r.id}`}
                >
                  <div
                    className="h-full bg-white border-2 border-black p-6 sm:p-8 md:p-10 flex flex-col"
                    style={{ minHeight: 'clamp(360px, 32vw, 440px)' }}
                  >
                    {/* Aspas */}
                    <div
                      className="text-black leading-none mb-5 select-none"
                      style={{
                        fontFamily: "'Georgia', serif",
                        fontSize: 'clamp(64px, 5.5vw, 96px)',
                        fontWeight: 700,
                        lineHeight: '0.7'
                      }}
                      aria-hidden="true"
                    >
                      “
                    </div>

                    {/* Classificação em estrelas */}
                    {typeof r.rating === 'number' && r.rating > 0 && (
                      <div
                        className="flex items-center gap-1 mb-3"
                        aria-label={`${r.rating} de 5 estrelas`}
                        data-testid={`review-rating-${r.id}`}
                      >
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            strokeWidth={1.5}
                            className={i < Math.round(r.rating) ? 'text-yellow-500' : 'text-gray-300'}
                            fill={i < Math.round(r.rating) ? '#facc15' : 'transparent'}
                          />
                        ))}
                      </div>
                    )}

                    {/* Texto da review */}
                    <p
                      className="text-gray-700 flex-1"
                      style={{
                        ...bodyFont,
                        fontSize: 'clamp(13px, 1vw, 15px)'
                      }}
                    >
                      {r.text}
                    </p>

                    {/* Separador */}
                    <hr className="mt-6 mb-4 border-0 border-t border-black/30" />

                    {/* Nome + data */}
                    <div className="flex items-baseline justify-between gap-3">
                      <p
                        className="text-black uppercase"
                        style={{
                          ...bebas,
                          fontSize: 'clamp(16px, 1.2vw, 19px)'
                        }}
                      >
                        {r.name}
                      </p>
                      {r.dateLabel && (
                        <span
                          className="text-gray-500"
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 300,
                            fontSize: '11px',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase'
                          }}
                        >
                          {r.dateLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Setas laterais (apenas desktop, quando há mais que uma posição) */}
          {totalSlides > 1 && (
            <>
              <button
                type="button"
                aria-label="Review anterior"
                data-testid="reviews-prev"
                onClick={prev}
                disabled={index === 0}
                className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-2 lg:-left-6 items-center justify-center w-12 h-12 bg-yellow-400 border-2 border-yellow-400 text-black hover:bg-black hover:text-yellow-400 hover:border-black disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-yellow-400 disabled:hover:text-black disabled:hover:border-yellow-400 transition-colors"
                style={{ borderRadius: 0 }}
              >
                <ChevronLeft size={22} strokeWidth={2.25} />
              </button>
              <button
                type="button"
                aria-label="Próxima review"
                data-testid="reviews-next"
                onClick={next}
                disabled={index === totalSlides - 1}
                className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-2 lg:-right-6 items-center justify-center w-12 h-12 bg-yellow-400 border-2 border-yellow-400 text-black hover:bg-black hover:text-yellow-400 hover:border-black disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-yellow-400 disabled:hover:text-black disabled:hover:border-yellow-400 transition-colors"
                style={{ borderRadius: 0 }}
              >
                <ChevronRight size={22} strokeWidth={2.25} />
              </button>
            </>
          )}
        </div>

        {/* Pontos de navegação */}
        {totalSlides > 1 && (
          <div
            className="mt-8 md:mt-10 flex items-center justify-center gap-2"
            data-testid="reviews-dots"
          >
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Ir para review ${i + 1}`}
                aria-current={index === i}
                data-testid={`reviews-dot-${i}`}
                onClick={() => goTo(i)}
                className="transition-all duration-300 hover:scale-110"
                style={{
                  width: '12px',
                  height: '12px',
                  border: '1.5px solid #000000',
                  backgroundColor: index === i ? '#000000' : 'transparent',
                  borderRadius: 0,
                  padding: 0,
                  cursor: 'pointer'
                }}
              />
            ))}
          </div>
        )}

        {/* CTA: Ver mais reviews no Google */}
        <div className="mt-10 md:mt-14 flex justify-center">
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-cta"
            className="inline-flex items-center gap-3 bg-black text-white border-2 border-black hover:bg-yellow-400 hover:text-black hover:border-yellow-400 transition-colors px-6 sm:px-8 py-3 sm:py-4 uppercase"
            style={{ ...bebas, fontSize: 'clamp(15px, 1.1vw, 18px)' }}
          >
            <span>Ver mais reviews no Google</span>
            <GoogleG />
          </a>
        </div>
      </div>
    </section>
  );
};

/* Pequena marca "Google" com cores oficiais */
const GoogleWordmark = () => (
  <span
    className="inline-flex items-baseline"
    style={{ fontFamily: "'Arial', sans-serif", fontWeight: 600, fontSize: '15px', letterSpacing: 0 }}
    aria-label="Google"
  >
    <span style={{ color: '#4285F4' }}>G</span>
    <span style={{ color: '#EA4335' }}>o</span>
    <span style={{ color: '#FBBC05' }}>o</span>
    <span style={{ color: '#4285F4' }}>g</span>
    <span style={{ color: '#34A853' }}>l</span>
    <span style={{ color: '#EA4335' }}>e</span>
  </span>
);

/* Ícone "G" da Google a cores */
const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.6-5.2-11.6-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.8 6.5 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.7 0 19.5-8.7 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.6l6.6 4.8C14.6 16.1 18.9 13.5 24 13.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.8 7.5 29.1 5.5 24 5.5c-7.4 0-13.8 4.2-17.7 10.1z"/>
    <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13.1-5.1l-6.1-5c-2 1.4-4.4 2.1-7 2.1-5.3 0-9.7-3.1-11.3-7.5l-6.6 5.1C9.8 39.1 16.3 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.4l6.1 5C40.3 35.4 43.5 30.2 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

export default Reviews;
